import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { withPageSecurity } from "../../util/script/RenderSecure";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const AuditTrail = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detail, setDetail] = useState(null);

  const parseData = (v) => {
    if (v === null || v === undefined || v === "") return null;
    if (typeof v === "object") return v;
    try { return JSON.parse(v); } catch { return v; }
  };

  const renderData = (obj) => {
    if (obj === null) return <div className="tx-muted tx-12 p-2">—</div>;
    if (typeof obj !== "object") return <pre className="tx-12 mb-0 p-2">{String(obj)}</pre>;
    return (
      <table className="table table-sm table-bordered mb-0">
        <tbody>
          {Object.entries(obj).map(([k, val]) => (
            <tr key={k}>
              <td className="fw-semibold tx-12" style={{ width: "40%" }}>{k}</td>
              <td className="tx-12">{val === null ? "—" : typeof val === "object" ? JSON.stringify(val) : String(val)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  useEffect(() => { fetchAuditTrail(); }, [filterAction, fromDate, toDate]);

  const fetchAuditTrail = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listAuditTrail({ action: filterAction || undefined, from_date: fromDate || undefined, to_date: toDate || undefined });
    if (res.status === "OK") setRecords(res.details?.list || (Array.isArray(res.details) ? res.details : []));
    else toast.error(res.message);
    setLoading(false);
  };

  const actionBadge = (action) => {
    const map = { CREATE: "success", UPDATE: "warning", DELETE: "danger", POST: "primary", REVERSE: "info", APPROVE: "success", VOID: "dark", LOGIN: "secondary" };
    return <span className={`badge bg-${map[action?.toUpperCase()] || "secondary"}`}>{action}</span>;
  };

  const columns = [
    { key: "created_at", label: "Time", render: (v) => <span className="tx-12">{v ? new Date(v).toLocaleString() : "-"}</span> },
    { key: "action", label: "Action", render: (v) => actionBadge(v) },
    { key: "table_name", label: "Entity", render: (v) => <code className="tx-12">{v}</code> },
    { key: "record_id", label: "Record ID" },
    { key: "performed_by_name", label: "User" },
    { key: "description", label: "Description", render: (v) => <span className="tx-12">{v}</span> },
    {
      key: "id", label: "",
      render: (id, row) => (
        <button className="btn btn-sm btn-light" onClick={() => setDetail(row)} title="Details"><i className="fa-solid fa-circle-info"></i></button>
      ),
    },
  ];

  const actionOptions = [{ value: "", label: "All Actions" }, "CREATE", "UPDATE", "DELETE", "POST", "REVERSE", "APPROVE", "VOID"].map((a) => typeof a === "object" ? a : { value: a, label: a });

  return (
    <AppContainer
      title="Audit Trail"
      previousPageTitle="Accounting"
      description="A complete tamper-evident log of all accounting actions. Every create, update, post, approval, and deletion is recorded here with the acting user and timestamp."
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">Action</label>
            <Select2 options={actionOptions} value={filterAction} onChange={setFilterAction} placeholder="All Actions" />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">From Date</label>
            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">To Date</label>
            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="card custom-card p-3">
        <DataTable columns={columns} data={records} loading={loading} pageSize={20} emptyMessage="No audit records found." />
      </div>
      {detail && (() => {
        const before = parseData(detail.before_data ?? detail.old_values ?? detail.old_data);
        const after = parseData(detail.after_data ?? detail.new_values ?? detail.new_data ?? detail.changes);
        return (
          <AppModal
            title={<>Audit Record #{detail.id}</>}
            size="lg"
            onClose={() => setDetail(null)}
            footer={
              <>
                <button className="btn btn-light" onClick={() => setDetail(null)}>Close</button>
              </>
            }
          >
            <div className="row g-2 mb-3">
              <div className="col-lg-3"><div className="tx-11 tx-muted">Action</div>{actionBadge(detail.action)}</div>
              <div className="col-lg-3"><div className="tx-11 tx-muted">Entity</div><code className="tx-12">{detail.table_name} #{detail.record_id}</code></div>
              <div className="col-lg-3"><div className="tx-11 tx-muted">User</div><div className="tx-13">{detail.performed_by_name || "—"}</div></div>
              <div className="col-lg-3"><div className="tx-11 tx-muted">Time</div><div className="tx-12">{detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"}</div></div>
              {detail.description && <div className="col-12"><div className="tx-11 tx-muted">Description</div><div className="tx-13">{detail.description}</div></div>}
              {detail.ip_address && <div className="col-12"><div className="tx-11 tx-muted">IP Address</div><code className="tx-12">{detail.ip_address}</code></div>}
            </div>
            {(before || after) ? (
              <div className="row g-3">
                <div className="col-lg-6">
                  <div className="fw-semibold tx-12 tx-uppercase tx-muted mb-1">Before</div>
                  {renderData(before)}
                </div>
                <div className="col-lg-6">
                  <div className="fw-semibold tx-12 tx-uppercase tx-muted mb-1">After</div>
                  {renderData(after)}
                </div>
              </div>
            ) : (
              <div className="tx-muted tx-12">No before/after snapshot recorded for this action.</div>
            )}
          </AppModal>
        );
      })()}
    </AppContainer>
  );
};

// VIEW-ACCOUNTS rather than the "ACC-AUDIT" this page arrived with — see the
// note on SystemConfig: no user in this system holds an ACC-* code, so the page
// rendered nothing at all.
export default withPageSecurity(AuditTrail, "VIEW-ACCOUNTS");
